/**
 * DownloadSheet — bottom sheet with per-platform download options.
 *
 * iter-73: replaces the legacy "save for one platform" 4-tile grid that
 * was nested deep on CardDetail. Per the spec, we don't say "Export."
 * We say "Download" and present sizes as a compact, premium grid:
 *   Story (9:16)  TikTok (9:16)  Feed (1:1)  Portrait (4:5)
 *   X (16:9)      HD (1080p)
 *
 * Each tile calls the parent's onPick(platformKey). The parent owns the
 * actual file generation (server-rendered Playwright PNG).
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Instagram, Download as DownloadIcon } from "lucide-react";
import { track } from "../../lib/track";

const PLATFORMS = [
    { key: "story",  label: "Instagram Story", size: "9:16", testid: "download-story" },
    { key: "tiktok", label: "TikTok Vertical", size: "9:16", testid: "download-tiktok" },
    { key: "feed",   label: "Instagram Feed",  size: "1:1",  testid: "download-feed" },
    { key: "port",   label: "Portrait",        size: "4:5",  testid: "download-port" },
    { key: "x",      label: "Facebook / X",    size: "16:9", testid: "download-x" },
    { key: "hd",     label: "HD Download",     size: "1080p", testid: "download-hd" },
];

export default function DownloadSheet({ open, onClose, onPick, exporting }) {
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 z-50 bg-black/65 backdrop-blur-sm"
                        data-testid="download-sheet-backdrop"
                    />
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed left-0 right-0 bottom-0 z-50 pointer-events-none"
                        data-testid="download-sheet"
                    >
                        <div className="pointer-events-auto mx-auto w-full max-w-md rounded-t-3xl bg-[#0f0f12] border-t border-white/10 shadow-2xl overflow-hidden">
                            <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                                <div>
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-amber-300/85">download</div>
                                    <div className="font-display text-lg text-white/95 mt-0.5">pick a size</div>
                                </div>
                                <button
                                    onClick={onClose}
                                    aria-label="close"
                                    className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/85 transition-colors"
                                    data-testid="download-sheet-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="px-4 pb-5 grid grid-cols-2 gap-2.5">
                                {PLATFORMS.map((p) => (
                                    <motion.button
                                        key={p.key}
                                        whileTap={{ scale: 0.97 }}
                                        disabled={exporting}
                                        onClick={() => { track("download_platform_picked", { platform: p.key }); onPick(p.key); }}
                                        className="flex items-center gap-3 rounded-xl bg-white/4 hover:bg-white/8 border border-white/8 hover:border-amber-300/35 px-3 py-3 transition-colors disabled:opacity-50 disabled:cursor-wait text-left"
                                        data-testid={p.testid}
                                    >
                                        <div className="w-9 h-9 shrink-0 rounded-lg bg-amber-400/10 border border-amber-300/20 flex items-center justify-center">
                                            <DownloadIcon className="w-4 h-4 text-amber-300" strokeWidth={1.6} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[12px] text-white/95 font-medium truncate">{p.label}</div>
                                            <div className="text-[10px] tracking-[0.16em] uppercase text-white/45 mt-0.5">{p.size}</div>
                                        </div>
                                    </motion.button>
                                ))}
                            </div>
                            <div className="pb-[max(env(safe-area-inset-bottom),0.75rem)]" />
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
